import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Toaster } from "@/components/ui/toaster";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ProviderLogin from "@/pages/ProviderLogin";
import AcceptInvite from "@/pages/AcceptInvite";
import RequestProviderAccess from "@/pages/RequestProviderAccess";
import Home from "@/pages/Home";
import Messages from "@/pages/Messages";
import Appointments from "@/pages/Appointments";
import Meds from "@/pages/Meds";
import Progress from "@/pages/Progress";
import Journaling from "@/pages/Journaling";
import Documents from "@/pages/Documents";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Resources from "@/pages/Resources";
import Forum from "@/pages/Forum";
import ForumThread from "@/pages/ForumThread";
import Forms from "@/pages/Forms";
import ProviderDashboard from "@/pages/ProviderDashboard";
import Onboarding from "@/pages/Onboarding";
import Privacy from "@/pages/Privacy";
import SpruceLogs from "@/pages/SpruceLogs";
import ProviderAuthCallback from "@/pages/ProviderAuthCallback";
import BiometricEnrollPrompt from "@/components/BiometricEnrollPrompt";
import useIdleTimeout from "@/hooks/useIdleTimeout";
import "@/hooks/useTheme"; // side-effect: applies persisted theme to <html> on first paint

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  useIdleTimeout();
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/provider-login" element={user ? <Navigate to={user.role === "admin" ? "/provider" : "/"} replace /> : <ProviderLogin />} />
      <Route path="/provider-auth-callback" element={<ProviderAuthCallback />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/request-provider-access" element={<RequestProviderAccess />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/onboarding" element={user ? <Onboarding /> : <Navigate to="/login" replace />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/" element={<Home />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/refill" element={<Meds initialTab="refills" />} />
        <Route path="/medications" element={<Meds />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/journaling" element={<Journaling />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/forum/:threadId" element={<ForumThread />} />
        <Route path="/provider" element={<ProviderDashboard />} />
        <Route path="/provider/spruce-logs" element={<SpruceLogs />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BiometricEnrollPrompt />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
